import type { AxiosError } from 'axios';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { AlbumType } from '@/lib/albums/types';

import { ALBUMS } from '@/routes/apiEndpoints';

export type AlbumsDataType = {
    data: AlbumType[];
    cursor: string;
};

type UseAlbumsProps = {
    activeSPID: SocialProfileType['id'];
    limit: number;
    params?: {
        name?: string;
        /**
         * The type of album to filter by (Stories)
         */
        album_type?: string;
        /**
         * The type of album to filter by (Manual)
         */
        type?: string;
    };
    options: SWRInfiniteConfiguration<AlbumsDataType, AxiosError<Error>>;
    cancelToken?: AbortController;
    shouldFetch?: boolean;
};

const getKey = (
    pageIndex: number,
    previousPageData: AlbumsDataType,
    activeSPID: SocialProfileType['id'],
    limit: number,
    params: UseAlbumsProps['params']
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('per_page', limit.toString());

    if (params?.name) {
        queryParams.append('name', params.name);
    }

    if (params?.album_type) {
        queryParams.append('album_type', params.album_type);
    }

    if (params?.type) {
        queryParams.append('type', params.type);
    }

    // First page, we don't have `previousPageData`
    const baseUrl = injectEndpointUrlParams(ALBUMS, { SPID: activeSPID });
    const url = `${baseUrl}?${queryParams.toString()}`;

    if (pageIndex === 0) return [url, JSON.stringify(params)];

    // Add the cursor to the API endpoint
    return [`${url}&cursor=${previousPageData.cursor}`, JSON.stringify(params)];
};

const useAlbums = ({
    activeSPID,
    limit = 20,
    params = {},
    options = {},
    cancelToken,
    shouldFetch = true
}: UseAlbumsProps) => {
    const {
        data: albums,
        size,
        setSize,
        isLoading,
        mutate: mutateAlbums,
        error: albumsError
    } = useSWRInfinite<AlbumsDataType, AxiosError<Error>>(
        (pageIndex, previousPageData) =>
            shouldFetch ? getKey(pageIndex, previousPageData, activeSPID, limit, params) : null,
        ([url]: string[]) =>
            get(url, params, { signal: cancelToken?.signal }).then(
                (res: { data: AlbumsDataType }) => res.data
            ),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const isLoadingMore = size > 0 && albums && typeof albums[size - 1] === 'undefined';

    return {
        albums,
        mutateAlbums,
        albumsError,
        loadMoreAlbums: () => setSize(size + 1), // Function to load more albums (load more button)
        isLoadingInitialData: isLoading, // Check if it's the initial loading (not the load more)
        isLoadMore: albums && typeof albums[albums.length - 1]?.cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};
export default useAlbums;
